import { Skeleton } from 'primereact/skeleton';
import { useCallback, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Divider } from 'primereact/divider';
import { Tooltip } from 'primereact/tooltip';
import { useSetAtom } from 'jotai';
import { endpointPortfolioXlsxDownloadGET } from '~/api/def/endpoints/portfolioAnalysisEndpoint';
import {
  selectedTickerFilterAtom,
  selectWidgetAIAtom,
} from '~/atoms/dashboardAtom';
import { NoDataAvailableTemplate } from '../../common/NoDataAvailableTemplate';
import { WidgetHeader } from '../../common/WidgetHeader';
import { useWidgetPopout } from '~/hooks/useWidgetPopout';

interface AIRecommendation {
  action: 'REDUCE' | 'INCREASE' | 'MAINTAIN';
  ticker: string;
  currentAllocation: string;
  proposedAllocation: string;
  reason: string;
  ai_reasoning?: string;
  confidence: 'High' | 'Medium' | 'Low';
  companyName?: string | null;
  instrumentTicker?: string | null;
}

export type AIRecommendationsWidgetProps = {
  data: AIRecommendation[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  handleRemoveWidget?: (widgetId: string) => void;
  widgetId: string;
  title?: string;
  snapshotId?: string | null;
};

const ExpandableAIReasoning: React.FC<{ reasoning: string; index: number }> = ({
  reasoning,
  index: _index,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const shouldTruncate = reasoning.length > 200; // Approximate 4 lines
  const displayText =
    isExpanded || !shouldTruncate
      ? reasoning
      : reasoning.substring(0, 200) + '...';

  return (
    <div className="mb-3 rounded text-xs">
      <strong>AI Reasoning:</strong> {displayText}
      {shouldTruncate && (
        <Button
          link
          size="small"
          className="p-0 text-xs"
          style={{ color: 'var(--bq-color-primary)' }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </Button>
      )}
    </div>
  );
};

export const AIRecommendationsWidget: React.FC<
  AIRecommendationsWidgetProps
> = ({
  data,
  isLoading,
  error,
  refetch,
  handleRemoveWidget,
  widgetId,
  title = 'AI Analysis Results',
  snapshotId,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);

  const removeWidget = useCallback(
    () => handleRemoveWidget?.(widgetId),
    [widgetId, handleRemoveWidget],
  );

  // Add popout functionality
  const handlePopout = useWidgetPopout({
    widgetId,
    widgetComponent: AIRecommendationsWidget,
    title: title || 'AI Analysis Results',
    defaultWidth: 900,
    defaultHeight: 700,
    widgetProps: {
      data,
      isLoading,
      error,
      refetch,
      handleRemoveWidget,
      widgetId,
      title,
      snapshotId,
    },
  });
  const setSelectWidgetAI = useSetAtom(selectWidgetAIAtom);
  const setSelectedTickerFilter = useSetAtom(selectedTickerFilterAtom);

  const handleDownloadCsv = useCallback(async () => {
    if (!snapshotId) return;

    try {
      setIsDownloading(true);
      const blob = await endpointPortfolioXlsxDownloadGET(snapshotId);

      if (!blob) {
        console.error('No data received from xlsx endpoint');
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `portfolio-analysis-${snapshotId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download XLSX:', error);
    } finally {
      setIsDownloading(false);
    }
  }, [snapshotId]);

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        item.ticker.toLowerCase().includes(searchLower) ||
        item.reason.toLowerCase().includes(searchLower) ||
        item.action.toLowerCase().includes(searchLower) ||
        (item.ai_reasoning &&
          item.ai_reasoning.toLowerCase().includes(searchLower)) ||
        (item.companyName &&
          item.companyName.toLowerCase().includes(searchLower))
      );
    });
  }, [data, searchTerm]);

  const noData = useMemo(() => {
    return !filteredData.length;
  }, [filteredData]);

  if (isLoading) {
    return (
      <div className="p-3 h-full">
        <Skeleton className="h-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="Something went wrong."
          isError
        />
      </div>
    );
  }

  if (!snapshotId) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="No Snapshot Available."
          subTitle="Run portfolio analysis to generate snapshot"
        />
      </div>
    );
  }

  // Todo: why do we need !searchTerm here???
  if (noData && !searchTerm) {
    return (
      <div className="h-full hover-container" id={widgetId}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          title="No Recommendations Found."
          icon="pi pi-chart-line"
        />
      </div>
    );
  }

  const cardTitle = (recommendation: AIRecommendation) => {
    return (
      <div className="flex justify-content-between align-items-center">
        <div className="flex w-full justify-content-between align-items-center gap-2">
          <Badge
            value={recommendation.action}
            severity={getBadgeSeverity(recommendation.action)}
          ></Badge>
          <Badge
            key={recommendation.ticker}
            value={recommendation.ticker}
            severity="info"
            className="text-xs cursor-pointer ticker-badge bq-ticker-badge"
            data-pr-tooltip={
              recommendation.companyName || recommendation.ticker
            }
            onClick={() => {
              setSelectedTickerFilter(recommendation.ticker);
            }}
          />
        </div>
      </div>
    );
  };

  const getBadgeSeverity = (action: string) => {
    switch (action) {
      case 'REDUCE':
        return 'danger';
      case 'INCREASE':
        return 'success';
      case 'MAINTAIN':
        return 'info';
      default:
        return 'info';
    }
  };

  return (
    <div className="hover-container h-full flex flex-column" id={widgetId}>
      <WidgetHeader
        title={title}
        removeWidget={removeWidget}
        refetch={() => refetch()}
        setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
        onPopout={handlePopout}
        widgetId={widgetId}
        children={
          <div className="flex align-items-center">
            <Tooltip
              target=".download-xlsx-icon"
              content="Download portfolio analysis XLSX report"
              position="bottom"
              pt={{
                root: {
                  style: {
                    zIndex: 9999,
                  },
                },
              }}
            />
            {isDownloading ? (
              <i
                className="pi pi-spin pi-spinner download-xlsx-icon"
                style={{
                  color: 'var(--primary-color)',
                  fontSize: '1.25rem',
                  cursor: 'not-allowed',
                }}
                title="Downloading..."
                data-pr-tooltip="Downloading..."
                aria-label="Downloading"
              />
            ) : (
              <i
                className="pi pi-download download-xlsx-icon"
                style={{
                  color: 'var(--primary-color)',
                  fontSize: '1.25rem',
                  cursor: snapshotId ? 'pointer' : 'not-allowed',
                  opacity: snapshotId ? 1 : 0.5,
                }}
                title="Download portfolio analysis XLSX report"
                data-pr-tooltip="Download portfolio analysis XLSX report"
                onClick={(e) => {
                  if (snapshotId && !isDownloading) {
                    e.stopPropagation();
                    e.preventDefault();
                    handleDownloadCsv();
                  }
                }}
                onMouseEnter={(e) => {
                  if (snapshotId) {
                    e.currentTarget.style.opacity = '0.8';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = snapshotId ? '1' : '0.5';
                }}
                aria-label="Download portfolio analysis XLSX report"
              />
            )}
          </div>
        }
      />

      {/* Sticky Search Controls */}
      <div className="sticky top-0 z-1">
        <div className="flex flex-column gap-2 px-3 pt-3">
          <div className="p-inputgroup flex-1">
            <span className="p-inputgroup-addon">
              <i className="pi pi-search"></i>
            </span>
            <InputText
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by ticker, action, reason, or company..."
              className="flex-1"
              size="small"
            />
            {searchTerm && (
              <span
                className="p-inputgroup-addon cursor-pointer hover:bg-gray-600"
                onClick={() => setSearchTerm('')}
              >
                <i className="pi pi-times"></i>
              </span>
            )}
          </div>
          {searchTerm && (
            <div
              className="text-xs"
              style={{ color: 'var(--bq-text-tertiary)' }}
            >
              Showing {filteredData.length} of {data.length} recommendations
            </div>
          )}
        </div>
      </div>
      <div className="px-3">
        <Divider className="my-3" />
      </div>
      {/* Full Height Content with Scrollable Area */}
      <div className="flex-1 px-3 pb-3 overflow-y-auto">
        <div className="flex flex-column gap-4 h-full" style={{ minHeight: 0 }}>
          {noData ? (
            <div
              className="p-4 text-center"
              style={{ color: 'var(--bq-text-tertiary)' }}
            >
              <i className="pi pi-search text-2xl mb-2 block"></i>
              <p style={{ color: 'var(--bq-text-primary)' }}>
                No recommendations found matching "{searchTerm}"
              </p>
              <p
                className="text-sm mt-1"
                style={{ color: 'var(--bq-text-tertiary)' }}
              >
                Try adjusting your search terms
              </p>
            </div>
          ) : (
            filteredData.map((recommendation, index) => (
              <Card
                key={`${widgetId}-${recommendation.ticker}-${index}-${recommendation.currentAllocation}`}
                title={cardTitle(recommendation)}
                subTitle={`${recommendation.currentAllocation} → ${recommendation.proposedAllocation}`}
                className="h-full flex flex-column"
                pt={{
                  body: {
                    className: 'flex flex-column gap-2 flex-1',
                  },
                  subTitle: {
                    className: 'text-sm mb-0',
                    style: { color: 'var(--bq-text-tertiary)' },
                  },
                  content: {
                    className: 'p-0 m-0 flex-1',
                  },
                }}
              >
                <p className="mt-0">{recommendation.reason}</p>
                {recommendation.ai_reasoning && (
                  <ExpandableAIReasoning
                    reasoning={recommendation.ai_reasoning}
                    index={index}
                  />
                )}
                <span
                  className="text-xs"
                  style={{ color: 'var(--bq-text-tertiary)' }}
                >
                  Confidence: {recommendation.confidence}
                </span>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
